/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, FC } from 'react'
import { useRouter } from 'next/router'
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import get from 'lodash/get'
import { ProductByHandleQuery, ProductByHandleQueryVariables } from 'storefront'
import ProductsSwiper from '../../components/products/ProductsSwiper'
import ProductSelect from '../../components/products/ProductsSelect'
import ProductModel from '../../components/products/ProductsModel'
import ProductSizeGuides from '../../components/products/ProductsSizeGuide'
import ProductsMeta from '../../components/products/ProductsMeta'
import {
  CouponsList,
  SplitBar,
  DeliveryInfo,
  ProductFooter,
  ProductShare,
  BackToCollection,
} from '../../components/products/ProductsSection'
// import ProductsRecommend from '../../components/products/ProductsRecommend'
// import ProductsReviews from '../../components/products/ProductsReviews'
import useCart from '../../hooks/useCart'
import { ProductPageSensor } from '../../utils/bloomchicdata/page/productpage'
import productByHandle from '../../api/graphql/productByHandlerQuery.gql'
import initializeApollo from '../../api/client'
import { ProductSkeleton } from '../../components/skeleton'
import { useIsomorphicLayoutEffect } from '../../utils/helpers'
import { getProductIsAvailable, getProductSPU } from '../../utils/product'

const ProductsRecommend = dynamic(
  () => import('../../components/products/ProductsRecommend')
)
// const ProductsReviews = dynamic(
//   () => import('../../components/products/ProductsReviews')
// )
const ProductsReviewsNew = dynamic(
  () => import('../../components/products/ProductsReviewsNew')
)

const productPageSensorMod = () =>
  import('../../utils/bloomchicdata/page/productpage')
let productPageSensor: ProductPageSensor | null

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: true,
})

export const getStaticProps: GetStaticProps = async (context) => {
  const client = initializeApollo()
  const res = await client.query<
    ProductByHandleQuery,
    ProductByHandleQueryVariables
  >({
    variables: {
      handle: context?.params?.name as string,
    },
    query: productByHandle,
  })
  if (!res.data.product) {
    return {
      notFound: true,
    }
  }
  return {
    props: {
      product: res.data.product,
    },
    revalidate: 60 * 60,
  }
}
// ProductsPage Entry
const ProductsPage: FC<InferGetStaticPropsType<typeof getStaticProps>> = (
  props
) => {
  // const { seo, images } = props.product
  const router = useRouter()
  const { addLines, show, loading } = useCart()
  // const { name } = router.query
  // const [productInfo, setProductInfo] = useState<any>(null)
  const [moreProductInfo, setMoreProductInfo] = useState<any>({})
  const [selectInfo, setSelectInfo] = useState<any>({})
  const [swiperIndex, setSwiperIndex] = useState<number>(0)
  const [canBuy, setCanBuy] = useState<boolean>(true)
  const [openSizeGuide, setOpenSizeGuide] = useState<boolean>(false)
  const [openShare, setOpenShare] = useState<boolean>(false)
  const [reviews, setReviews] = useState({
    rating: -1,
    total: -1,
  })
  const [showLimitTips, setShowLimitTips] = useState<boolean>(false)
  // const { query } = useGetProductByHandler(name as string)
  // const info = get(query, 'data.product') as ProductByHandleQuery['product']
  const productInfo = useMemo(() => get(props, 'product'), [props])
  const seo = useMemo(() => get(props, 'product.seo'), [props])
  const fbImage = useMemo(
    () => get(props, 'product.images.edges[0].node.originalSrc', ''),
    [props]
  )
  // const pageLoading = useMemo(() => !router.isFallback, [router.isFallback])
  const handleAdd = async () => {
    const back = await addLines([selectInfo], true)
    productPageSensor?.AddToCartClick(productInfo?.title as string, selectInfo)
    window?.allPixels?.addToCart(productInfo, selectInfo)
    if (back) {
      show()
    } else {
      setShowLimitTips(true)
    }
  }
  /** 初始化选中sku信息 */
  const onSelectChange = (e?: any) => {
    setSelectInfo(e)
    setSwiperIndex(e?.swiperIndex)
    setCanBuy(e && e?.availableForSale)
  }

  useIsomorphicLayoutEffect(() => {
    if (process.browser && productInfo) {
      setMoreProductInfo({
        available: getProductIsAvailable(productInfo),
        spu: getProductSPU(productInfo),
      })
      if (process.browser) {
        window?.allPixels.viewProduct(productInfo)
      }
    }
  }, [productInfo])

  useIsomorphicLayoutEffect(() => {
    if (process.browser && productInfo && reviews.total !== -1) {
      productPageSensorMod().then((mod) => {
        if (window.scrollLock) return
        productPageSensor = new mod.ProductPageSensor()
        productPageSensor?.ProductPageShow(productInfo, reviews)
        window.productReportData = { productInfo, reviews }
      })
    }
  }, [productInfo, reviews.total])

  const Seo = () => (
    <Head>
      <title>{seo?.title}</title>
      {seo?.description && (
        <meta name="description" content={seo?.description} />
      )}
      {/* fackbook */}
      <meta property="og:site_name" content="BloomChic" />
      {/* <meta property="og:url" content={url} /> */}
      <meta property="og:title" content={seo?.title || 'BloomChic'} />
      <meta property="og:type" content="product" />
      {seo?.description && (
        <meta property="og:description" content={seo?.description} />
      )}
      {fbImage && (
        <>
          <meta property="og:image" content={fbImage} />
          <meta property="og:image:secure_url" content={fbImage} />
          <meta property="og:image:width" content="750" />
          <meta property="og:image:height" content="1000" />
        </>
      )}
      {/* twitter */}
      <meta name="twitter:site" content="@" />
      <meta name="twitter:card" content="summary_large_image" />
      {seo?.title && <meta name="twitter:title" content={seo?.title} />}
      {seo?.description && (
        <meta name="twitter:description" content={seo?.description} />
      )}
    </Head>
  )

  const errorMsg = useMemo(() => {
    // console.log('showlimitTips', showLimitTips, selectInfo, productInfo)
    if (showLimitTips && selectInfo && productInfo) {
      return ` All ${selectInfo?.quantityAvailable} ${productInfo.title} -
        ${selectInfo?.title} are in you cart`
    }
    return ''
  }, [showLimitTips, selectInfo, productInfo])

  useEffect(() => {
    setOpenSizeGuide(false)
    setShowLimitTips(false)
  }, [productInfo])
  return (
    <>
      {!router.isFallback && <Seo />}
      {(!productInfo || !selectInfo?.id) && (
        <div className="lg:container md:p-10 product-wrapper pb-10 transition-opacity delay-200">
          <div className="hidden md:block w-full bg-white">
            <ProductSkeleton isPC />
          </div>
          <div className="md:hidden w-full bg-white">
            <ProductSkeleton />
          </div>
        </div>
      )}
      {productInfo && (
        <div
          id="product"
          data-title={productInfo?.title}
          data-available={moreProductInfo.available}
          data-spu={moreProductInfo.spu}
          data-oprice={productInfo?.variants.edges[0].node.compareAtPrice}
          data-cprice={productInfo?.variants.edges[0].node.price}
          className="lg:container md:p-10 product-wrapper pb-10 opacity-0 transition-opacity delay-200"
          style={{
            opacity: selectInfo?.id ? 1 : 0,
          }}
          key={productInfo.title}
        >
          <div className="md:flex w-full clear-both items-start">
            <div
              className="w-full md:w-3/5 md:top-40 md:sticky md:float-left"
              id="pswiper"
            >
              <ProductsSwiper data={productInfo} initialSlide={swiperIndex} />
            </div>
            <div className="w-full md:w-2/5 float-none md:float-right">
              <ProductsMeta
                data={productInfo}
                selectInfo={selectInfo}
                onShare={() => setOpenShare(true)}
                reviews={reviews}
              >
                <ProductSelect
                  data={productInfo}
                  onSelectChange={(val) => onSelectChange(val)}
                  onOpenSizeGuide={() => setOpenSizeGuide(true)}
                  errorMsg={errorMsg}
                />
              </ProductsMeta>
              <SplitBar />
              <CouponsList />
              <SplitBar />
              <DeliveryInfo />
              <SplitBar />
              <ProductFooter
                loading={loading}
                canBuy={canBuy}
                onAddClick={handleAdd}
                forBtn
                errorMsg={errorMsg}
              />
              <ProductModel data={productInfo} />
              <SplitBar />
              {/* <ProductsReviews id={name as string} onInit={setReviews} /> */}
              <ProductsReviewsNew
                pData={{
                  ...productInfo,
                  ...moreProductInfo,
                  compareAtPrice:
                    +productInfo?.variants.edges[0].node.compareAtPrice,
                  price: +productInfo?.variants.edges[0].node.price,
                  pCover: productInfo?.images?.edges[0]?.node?.src ?? '',
                }}
                pTitle={productInfo?.title as string}
                pid={productInfo.id}
                pHandle={productInfo.handle}
                onInit={setReviews}
              />
              <SplitBar />
            </div>
          </div>
          <ProductShare
            open={openShare}
            onOpenChange={setOpenShare}
            data={productInfo}
          />
          <ProductFooter
            canBuy={canBuy}
            loading={loading}
            onAddClick={handleAdd}
          />
          <ProductSizeGuides
            data={productInfo}
            open={openSizeGuide}
            onBack={setOpenSizeGuide}
          />
          <ProductsRecommend
            proudctId={productInfo?.id}
            title="You May Also Like"
          />
          <BackToCollection />
        </div>
      )}
    </>
  )
}

export default ProductsPage
